/**
 * Configuracion de entorno con soporte runtime.
 *
 * Prioridad de lectura:
 *   1. window.__ENV__[key]  — inyectado por entrypoint.sh en Docker
 *   2. process.env.REACT_APP_{key} — inyectado por CRA en build time (dev local)
 *   3. fallback — valor por defecto
 */
const env = (key, fallback) => {
  const runtime = window.__ENV__?.[key];
  if (runtime !== undefined && runtime !== '') return runtime;
  const buildTime = process.env[`REACT_APP_${key}`];
  if (buildTime !== undefined && buildTime !== '') return buildTime;
  return fallback;
};

export const ENV = {
  // OIDC / Identity Manager
  OIDC_AUTHORITY:            env('OIDC_AUTHORITY', 'https://localhost:8443/realms/dev'),
  OIDC_CLIENT_ID:            env('OIDC_CLIENT_ID', 'boveda'),
  OIDC_REDIRECT_URI:         env('OIDC_REDIRECT_URI', 'http://localhost:3000'),
  OIDC_POST_LOGOUT_URI:      env('OIDC_POST_LOGOUT_URI', 'http://localhost:3000/'),
  OIDC_SCOPE:                env('OIDC_SCOPE', 'openid profile offline_access email'),
  // Cuanto antes del vencimiento se dispara la renovacion. Va por runtime y no hardcodeado
  // porque depende del IdP de cada instalacion. El principio —cada instalacion controla lo suyo sin
  // re-deploy de codigo— esta enunciado en ADR-024, cuyo alcance es firma; para la config de la UI
  // no hay ADR propio todavia (anotado en el backlog):
  // Azure AD emite tokens de 60-90 min, los realms de Keycloak del repo de 900 s, y el default
  // de Keycloak es 60 s. Regla: nunca mas de la mitad de la vida del token — si
  // expires_in - margen <= 0, oidc-client-ts clampea el timer a 1 segundo y la renovacion entra
  // en loop contra el IdP del cliente (#168).
  OIDC_TOKEN_EXPIRING_NOTIFICATION_S: env('OIDC_TOKEN_EXPIRING_NOTIFICATION_S', '300'),

  // API
  API_URL:                   env('API_URL', ''),

  // Cliente local de firma (#59): URL del daemon. Default HTTP loopback 12555 (ADR-035).
  // http://127.0.0.1 es "potentially trustworthy" → un fetch desde la UI HTTPS NO es mixed-content
  // (exento por spec) y evita el problema del cert self-signed no confiable del canal HTTPS 12557
  // (que quedó deprecado). Se usa 127.0.0.1 literal (no "localhost") para evitar rarezas de
  // resolución. El daemon 0.1.10+ responde el header PNA (Access-Control-Allow-Private-Network).
  // 18555 y no 12555 (#109): ese puerto lo ocupa el firmador de GDE, que convive con Bóveda en
  // las máquinas de los organismos. Overridable en runtime por entrypoint.sh → window.__ENV__.
  SIGNATURE_CLIENT_DAEMON_URL: env('SIGNATURE_CLIENT_DAEMON_URL', 'http://127.0.0.1:18555'),

  // Firma masiva (#59): tuning del loop de firma sin recompilar. CHUNK_SIZE = digests firmados por
  // sesión de token en cada /sign-batch; SIGN_DELAY_MS = pausa entre chunks (0 = sin pausa). El
  // throttle de 5s previo era una mitigación de la teoría equivocada "el token falla en ráfaga" que
  // el fix #98 desmintió (el fallo era server-side); default sin pausa y chunks moderados.
  SIGNATURE_BATCH_CHUNK_SIZE:  env('SIGNATURE_BATCH_CHUNK_SIZE', '50'),
  SIGNATURE_BATCH_SIGN_DELAY_MS: env('SIGNATURE_BATCH_SIGN_DELAY_MS', '0'),

  // Claim prefixes
  IM_CLAIM_ROLE_PREFIX:      env('IM_CLAIM_ROLE_PREFIX', 'boveda:role:'),
  IM_CLAIM_GROUPS_PREFIX:    env('IM_CLAIM_GROUPS_PREFIX', 'boveda:group:'),
  IM_CLAIM_CLEARANCE_PREFIX: env('IM_CLAIM_CLEARANCE_PREFIX', 'boveda:clearance:'),

  // Branding
  APP_NAME:                  env('APP_NAME', 'Bóveda Documental'),
  ORG_NAME:                  env('ORG_NAME', 'Universidad de Buenos Aires'),
  ORG_SHORT:                 env('ORG_SHORT', 'UBA'),
  LOGO_URL:                  env('LOGO_URL', ''),
  LOGO_ALT:                  env('LOGO_ALT', 'Logo'),
  LANDING_TITLE:             env('LANDING_TITLE', env('APP_NAME', 'Bóveda Documental')),
  LANDING_SUBTITLE:          env('LANDING_SUBTITLE', ''),
  PERMISSIONS_PROCESS_URL:   env('PERMISSIONS_PROCESS_URL', ''),
};
